import { apolloClient } from '../api/client.js';
import { GENERATE_VIEW_URL } from '../../graphql/mutations/index.js';

const cache = new Map();

function isAbsoluteHttpUrl(value) {
  return typeof value === 'string' && /^https?:\/\//i.test(value);
}

function isGcsHost(url) {
  return url?.hostname?.toLowerCase() === 'storage.googleapis.com';
}

function normalizeObjectPath(ref) {
  if (typeof ref !== 'string') return ref;
  let next = ref.trim();
  // Common backend variants: "/files/...", "bucket/files/..."
  next = next.replace(/^\/+/, '');
  next = next.replace(/^yocas-storage\/+/, '');
  return next;
}

function looksLikeObjectPath(value) {
  if (typeof value !== 'string') return false;
  const v = value.trim();
  if (!v) return false;
  // Common prefixes we generate/store
  if (v.startsWith('files/') || v.startsWith('images/')) return true;
  // Any nested path strongly suggests an object key
  if (v.includes('/')) return true;
  // If it has a file extension, treat as object key
  if (/\.[a-z0-9]{2,6}$/i.test(v)) return true;
  return false;
}

function tryExtractObjectPathFromRef(ref) {
  if (typeof ref !== 'string') return null;

  // gs://bucket/path/to/file
  const gsMatch = ref.match(/^gs:\/\/([^/]+)\/(.+)$/i);
  if (gsMatch?.[2]) return gsMatch[2];

  if (!isAbsoluteHttpUrl(ref)) return null;

  try {
    const url = new URL(ref);
    if (!isGcsHost(url)) return null;
    // https://storage.googleapis.com/<bucket>/<objectPath>
    const parts = url.pathname.replace(/^\/+/, '').split('/');
    if (parts.length < 2) return null;
    return parts.slice(1).join('/');
  } catch {
    return null;
  }
}

function isSignedGcsUrl(ref) {
  if (typeof ref !== 'string' || !isAbsoluteHttpUrl(ref)) return false;
  try {
    const url = new URL(ref);
    if (!isGcsHost(url)) return false;
    // Signed URL indicators
    return url.searchParams.has('X-Goog-Signature') || url.searchParams.has('GoogleAccessId');
  } catch {
    return false;
  }
}

/**
 * Resolve a stored file reference into a signed read URL.
 * - If `ref` is an http(s) URL, it is returned as-is (legacy/public).
 * - Otherwise, it is treated as `objectPath` and exchanged for a signed URL.
 *
 * @param {string | null | undefined} ref
 * @returns {Promise<string | null>}
 */
export async function resolveSecureUrl(ref) {
  if (!ref) return null;

  // If backend returns a signed GCS URL, it can be used directly.
  if (isSignedGcsUrl(ref)) return ref;

  // If backend returns an unsigned GCS URL (or gs://...), prefer exchanging to a signed view URL.
  const extractedFromRef = tryExtractObjectPathFromRef(ref);
  const objectPath = normalizeObjectPath(extractedFromRef || ref);

  // For non-GCS absolute URLs (legacy/public), return as-is.
  if (isAbsoluteHttpUrl(ref) && !extractedFromRef) return ref;

  // Guardrail: avoid trying to sign non-file strings like category icon names (e.g. "campaign").
  if (!extractedFromRef && !isAbsoluteHttpUrl(ref) && !looksLikeObjectPath(objectPath)) {
    return null;
  }

  const cached = cache.get(objectPath);
  if (cached && cached.expiresAtMs > Date.now() + 5_000) {
    return cached.url;
  }

  const { data } = await apolloClient.mutate({
    mutation: GENERATE_VIEW_URL,
    variables: { input: { objectPath } }
  });

  const payload = data?.generateViewUrl;
  if (!payload?.signedUrl || !payload?.expiresAt) {
    throw new Error('Failed to resolve secure URL');
  }

  const expiresAtMs = Date.parse(payload.expiresAt);
  cache.set(objectPath, {
    url: payload.signedUrl,
    expiresAtMs: Number.isFinite(expiresAtMs) ? expiresAtMs : Date.now(),
  });

  return payload.signedUrl;
}

